import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faCog,
  faCopy,
  faLink,
  faRotateRight,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { AgentOutputSchemaEditor } from "src/agents/AgentOutputSchemaEditor";
import { AgentVersionStatusIndicator } from "src/agents/AgentVersionName";
import { CreateAgentVersionModal } from "src/agents/CreateAgentVersionModal";
import {
  useAgent,
  useAgentVersion,
  useAgentVersions,
  usePublishAgentVersion,
  useUpdateAgentVersionConfiguration,
} from "src/agents/queries";
import { AgentVersion, AgentVersionConfigurationInput } from "src/agents/types";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { DisplayDateFromNow } from "src/base-components/Date";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { FormItem } from "src/base-components/FormItem";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Textarea } from "src/base-components/Textarea";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { DashboardContent } from "src/layout/DashboardContent";
import { NavItem, NavSidebar } from "src/layout/NavSideBar";
import { PageHeader } from "src/layout/PageHeader";
import {
  AgentVersionPageParams,
  DashboardPageParams,
  getBaseUrl,
  getUrlToAgentsPage,
  getUrlToAgentVersionPage,
  getUrlToAgentVersionsPage,
} from "src/router/urls";
import { copyTextToClipboard } from "src/utils/clipboard";
import { debounceAndSaveArgs } from "src/utils/debounceAndSaveArgs";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentVersionPage: React.FC = () => {
  useDocumentTitle(["Edit Agent Version", "Agents"]);

  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();
  const navigate = useNavigate();

  const agentQuery = useAgent(agentId);
  const versionsQuery = useAgentVersions(agentId);
  const versionQuery = useAgentVersion(agentId, versionId);
  const publishMutation = usePublishAgentVersion();

  const {
    isOpen: isDuplicateModalOpen,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
  } = useModal();

  const currentVersion = versionQuery.data;

  const handlePublish = async () => {
    if (!currentVersion) return;

    try {
      await publishMutation.mutateAsync({
        agentId,
        versionId,
        etag: currentVersion.etag,
      });
      toastActions.success({
        title: "Agent version published successfully",
      });
    } catch (error) {
      toastActions.failure({
        title: "Failed to publish agent version",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  };

  const getPageHeaderTitle = () => {
    if (agentQuery.isLoading) {
      return (
        <div className="flex items-center gap-x-2">
          <SkeletonPlaceholder height="h-5" width="w-36" />
          <Icon icon={faChevronRight} size="xs" />
          <SkeletonPlaceholder height="h-5" width="w-36" />
        </div>
      );
    }

    if (agentQuery.isSuccess && agentQuery.data) {
      return (
        <div className="flex items-center gap-x-2">
          <Link
            className="flex items-center gap-x-2"
            to={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
          >
            <div className="max-w-96 truncate">{agentQuery.data.name}</div>{" "}
          </Link>
          <Icon icon={faChevronRight} size="xs" />
          <VersionPicker versions={versionsQuery.data ?? []} />
        </div>
      );
    }

    const isNotFound = false; // Check error type when real API is available

    return isNotFound ? "Agent not found" : "Error loading agent";
  };

  return (
    <DashboardContent
      Header={
        <PageHeader
          backLink={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
          sideContent={
            <div className="flex items-center gap-x-3">
              {agentQuery.isLoading ? (
                <SkeletonPlaceholder height="h-5" width="w-36" />
              ) : (
                currentVersion && <UpdatedInfo version={currentVersion} />
              )}
              {currentVersion?.status === "draft" && (
                <Button
                  disabled={publishMutation.isPending}
                  loading={publishMutation.isPending}
                  size="sm"
                  onClick={handlePublish}
                >
                  Publish Agent
                </Button>
              )}
              <PageMenu onDuplicate={openDuplicateModal} />
            </div>
          }
          title={getPageHeaderTitle()}
        />
      }
      sidebar={<AgentVersionSidebar />}
      removePadding
    >
      <div
        className="flex h-full flex-col bg-gray-50"
        data-loc="agent-version-page"
      >
        {versionQuery.error ? (
          <AgentVersionErrorState
            error={versionQuery.error}
            refetch={versionQuery.refetch}
          />
        ) : (
          <div className="decideScrollbar compensate-scrollbar-gutter flex flex-1 flex-col gap-x-5 bg-gray-50 pt-10">
            <div className="ml-12 mr-10 min-w-[700px] flex-1">
              {versionQuery.isLoading || !currentVersion ? (
                <AgentVersionConfigurationFormSkeleton />
              ) : (
                <AgentVersionConfigurationForm
                  key={currentVersion.id}
                  version={currentVersion}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <CreateAgentVersionModal
        agentId={agentId}
        open={isDuplicateModalOpen}
        startFromScratch={false}
        versionId={versionId}
        onClose={closeDuplicateModal}
        onCreateSuccess={(createdAgentId, newVersion) =>
          navigate(
            getUrlToAgentVersionPage(
              orgId,
              wsId,
              createdAgentId,
              newVersion.id,
            ),
          )
        }
      />
    </DashboardContent>
  );
};

const AgentVersionErrorState: React.FC<{
  error: Error;
  refetch: () => void;
}> = ({ refetch }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const isNotFoundError = false; // Check error type when real API is available

  if (isNotFoundError) {
    return (
      <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
        <WhitePane>
          <EmptyState
            actionButton={{
              text: "Go to Agents",
              onClick: () => navigate(getUrlToAgentsPage({ orgId, wsId })),
            }}
            description="The agent you are looking for does not exist."
            headline="Agent not found"
            icon={faRotateRight}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <div className="m-auto flex w-[1084px] flex-1 gap-x-5 bg-gray-50 py-10">
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
          }}
          description="We're having trouble loading Agent Version. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading Agente Version"
          icon={faRotateRight}
          variant="error"
        />
      </WhitePane>
    </div>
  );
};

type VersionPickerProps = {
  versions: AgentVersion[];
};

const VersionPicker: React.FC<VersionPickerProps> = ({ versions }) => {
  const navigate = useNavigate();
  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();

  return (
    <FixedPositionedDropdown
      buttonClassName="rounded-full border-none max-w-64"
      dataLoc="current-version-picker"
      elements={versions.map((version) => ({
        key: version.id,
        value: version,
      }))}
      placement="bottom start"
      renderButtonValue={(version) =>
        version ? (
          <Pill
            dataLoc="current-version-picker-button"
            size="lg"
            variant="outlined-white"
            fullWidth
          >
            <AgentVersionStatusIndicator status={version.status} />
            <Pill.Text>{version.name}</Pill.Text>
            <Pill.Icon icon={faChevronDown} size="base" />
          </Pill>
        ) : (
          <SkeletonPlaceholder height="h-5" width="w-36" />
        )
      }
      renderValue={({ value: version, selected }) => (
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-1.5">
            <AgentVersionStatusIndicator status={version.status} />
            <span>{version.name}</span>
          </div>
          {selected && (
            <Icon color="text-indigo-500" icon={faCheck} size="sm" />
          )}
        </div>
      )}
      selected={versionId}
      onSelect={(versionId) =>
        navigate(getUrlToAgentVersionPage(orgId, wsId, agentId, versionId))
      }
    />
  );
};

const UpdatedInfo: React.FC<{
  version: AgentVersion | undefined;
}> = ({ version }) => {
  if (!version) {
    return null;
  }

  const dateValue =
    version.status === "published"
      ? version.metadata.published_at
      : version.metadata.updated_at;
  const prefix = version.status === "published" ? "Published" : "Updated";

  if (!dateValue) {
    return null;
  }

  return (
    <Pill
      dataLoc="last-updated-info"
      variant={version.status === "published" ? "green" : "indigo-light"}
    >
      <Pill.Icon icon={faCheck} />
      <Pill.Text fontType="text">
        {prefix} <DisplayDateFromNow value={dateValue} />
      </Pill.Text>
    </Pill>
  );
};

const PageMenu: React.FC<{
  onDuplicate: () => void;
}> = ({ onDuplicate }) => {
  const { orgId, wsId, agentId, versionId } =
    useParamsDecode<AgentVersionPageParams>();

  const options = [
    {
      key: "Duplicate",
      icon: faCopy,
      action: onDuplicate,
    },
    {
      key: "Copy link",
      icon: faLink,
      action: () => {
        copyTextToClipboard(
          getBaseUrl() +
            getUrlToAgentVersionPage(orgId, wsId, agentId, versionId),
        );

        toastActions.success({
          title: "Link copied to clipboard",
        });
      },
    },
  ].filter(Boolean) as OptionsDropdownElement[];

  return (
    <EllipsisOptionsDropdown dataLoc="version-page-menu" elements={options} />
  );
};

type AgentVersionConfigurationFormProps = {
  version: AgentVersion;
};

// IMPORTANT! Changes to the layout of the form must be reflected in the skeleton placeholder below.
const AgentVersionConfigurationForm: React.FC<
  AgentVersionConfigurationFormProps
> = ({ version }) => {
  const formMethods = useForm<AgentVersionConfigurationInput>({
    defaultValues: version.configuration,
  });

  const isPublished = version.status === "published";

  const { save } = useSequentialAutoSave({
    agentId: version.agent_id,
    versionId: version.id,
    initialEtag: version.etag,
    disabled: isPublished,
  });

  // Subscribe to form changes and save changes to the server
  useEffect(() => {
    if (isPublished) return;

    const unsubscribe = formMethods.subscribe({
      formState: { values: true },
      callback: ({ values: configuration }) => save(configuration),
    });

    return () => {
      unsubscribe();
      save.flush(); // Flush debounce on unmount
    };
  }, [formMethods, isPublished, save]);

  return (
    <FormProvider {...formMethods}>
      <WhitePane noMinWidth>
        <div className="h-full w-full px-5 py-4">
          <div className="flex items-center justify-between border-b border-gray-200 pb-3">
            <h2 className="text-gray-800 font-inter-semibold-13px">
              Configure agent
            </h2>
          </div>
          <div className="mt-8 flex items-start gap-15">
            <h3 className="text-gray-800 font-inter-semibold-13px">Prompt</h3>
            <div className="h-90 flex-1">
              <Controller
                control={formMethods.control}
                name="model_config.system_prompt"
                render={({ field }) => (
                  <Textarea
                    disabled={isPublished}
                    value={field.value || ""}
                    fullHeight
                    fullWidth
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
          <div className="mt-8">
            <AgentToolsEditor immutable={isPublished} />
          </div>
          <div className="mt-8">
            <AgentOutputSchemaEditor immutable={isPublished} />
          </div>
        </div>
      </WhitePane>
    </FormProvider>
  );
};

const AgentVersionConfigurationFormSkeleton: React.FC = () => {
  return (
    <WhitePane noMinWidth>
      <div className="h-full w-full px-5 py-4">
        <h2 className="border-b border-gray-200 pb-3 text-gray-800 font-inter-semibold-13px">
          Configure agent
        </h2>
        <div className="mt-8 flex items-start gap-15">
          <h3 className="text-gray-800 font-inter-semibold-13px">Prompt</h3>
          <div className="h-90 flex-1">
            <SkeletonPlaceholder height="h-full" width="w-full" />
          </div>
        </div>
        <div className="mt-8">
          <SkeletonPlaceholder height="h-40" width="w-full" />
        </div>
        <div className="mt-8">
          <SkeletonPlaceholder height="h-40" width="w-full" />
        </div>
      </div>
    </WhitePane>
  );
};

type AgentToolsEditorProps = {
  immutable: boolean;
};

// Temporary component until we have a proper tools editor component.
const AgentToolsEditor: React.FC<AgentToolsEditorProps> = ({ immutable }) => {
  const { control } = useFormContext<AgentVersionConfigurationInput>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tools",
  });

  return (
    <Card className="w-full pb-4 pt-4">
      <FormItem>
        <div className="mb-2 flex items-center justify-between">
          <FormItem.Label description="Add tools for the agent to use. Each tool is defined as a JSON configuration object.">
            Tools
          </FormItem.Label>
          {!immutable && (
            <Button
              data-loc="add-tool-button"
              size="sm"
              variant="secondary"
              onClick={() =>
                append(
                  {} as NonNullable<
                    AgentVersionConfigurationInput["tools"]
                  >[number],
                )
              }
            >
              Add Tool
            </Button>
          )}
        </div>
        {fields.length === 0 && (
          <div className="text-gray-500 font-inter-medium-11px">
            No tools configured yet. Click "Add Tool" to add a tool
            configuration.
          </div>
        )}
        {fields.map((field, index) => (
          <div key={field.id} className="mb-4 space-y-2">
            <div className="flex items-center justify-between">
              <FormItem.Label className="mb-0">Tool {index + 1}</FormItem.Label>
              {!immutable && (
                <div className="cursor-pointer">
                  <Icon
                    color="text-gray-500 hover:text-gray-700"
                    data-loc={`remove-tool-button-${index}`}
                    icon={faTrashAlt}
                    onClick={() => remove(index)}
                  />
                </div>
              )}
            </div>
            <Controller
              control={control}
              name={`tools.${index}` as const}
              render={({ field: controllerField }) => (
                <div className="h-60">
                  <CodeEditor
                    backgroundColor="white"
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLine: true,
                    }}
                    dataLoc={`tool-json-editor-${index}`}
                    drawErrorBorder={false}
                    fontFamily="inter"
                    language="json"
                    readOnly={immutable}
                    value={
                      typeof controllerField.value === "string"
                        ? controllerField.value
                        : JSON.stringify(controllerField.value, null, 2)
                    }
                    lineWrapping
                    onChange={(value) => {
                      if (!value) {
                        controllerField.onChange({});
                        return;
                      }
                      try {
                        const parsed = JSON.parse(value);
                        controllerField.onChange(parsed);
                      } catch {
                        return; // silently fail for now
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>
        ))}
      </FormItem>
    </Card>
  );
};

const AgentVersionSidebar = () => {
  const { agentId, orgId, wsId, versionId } =
    useParamsDecode<AgentVersionPageParams>();
  const [collapsed, setCollapsed] = useLocalStorage(
    "agentVersionNavigationCollapsed",
    false,
  );

  return (
    <div className="border-x border-gray-100">
      <NavSidebar collapsed={collapsed} setCollapsed={setCollapsed}>
        <NavItem
          dataLoc="configure-agent"
          icon={faCog}
          title="Configure agent"
          to={getUrlToAgentVersionPage(orgId, wsId, agentId, versionId)}
        />
      </NavSidebar>
    </div>
  );
};

/**
 * Wraps useUpdateAgentVersionConfiguration with debounce + sequential queue logic.
 * - Uses the existing mutation
 * - Adds debounce to prevent excessive API calls
 * - Adds queue behavior via pendingConfigRef
 * - Tracks etag in ref to avoid stale closures
 */
type UseSequentialAutoSaveOptions = {
  agentId: string;
  versionId: string;
  initialEtag: string;
  disabled?: boolean;
};
const useSequentialAutoSave = ({
  agentId,
  versionId,
  initialEtag,
  disabled,
}: UseSequentialAutoSaveOptions) => {
  // Use the existing mutation - it already handles cache updates, optimistic updates, rollback
  const updateMutation = useUpdateAgentVersionConfiguration();

  // Stores latest data waiting to be sent if there's already an ongoing save call
  const pendingConfigRef = useRef<AgentVersionConfigurationInput | null>(null);

  // Track latest etag - updated after each successful save
  const etagRef = useRef(initialEtag);

  // Keep etag in sync when prop changes (from query cache updates)
  useEffect(() => {
    etagRef.current = initialEtag;
  }, [initialEtag]);

  const saveWithQueue = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      updateMutation.mutate(
        {
          agentId,
          versionId,
          configuration,
          etag: etagRef.current,
        },
        {
          onSuccess: (result) => {
            // Update etag ref with new value from server
            etagRef.current = result.etag;
          },
          onSettled: () => {
            // THE QUEUE LOGIC: When request finishes, check if there was any pending save call
            if (pendingConfigRef.current !== null) {
              const nextConfig = pendingConfigRef.current;
              pendingConfigRef.current = null;
              // Fire next save with fresh etag
              saveWithQueue(nextConfig);
            }
          },
          onError: (error) => {
            toastActions.failure({
              title: "Failed to save changes",
              description:
                error instanceof Error
                  ? error.message
                  : "An unknown error occurred",
            });
          },
        },
      );
    },
    [updateMutation, agentId, versionId],
  );

  // The trigger function - checks if busy before firing
  const save = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      if (disabled) return;

      if (updateMutation.isPending) {
        // BUSY: Queue it (overwrites previous - latest wins)
        pendingConfigRef.current = configuration;
      } else {
        // IDLE: Fire immediately
        saveWithQueue(configuration);
      }
    },
    [saveWithQueue, updateMutation, disabled],
  );

  // Debounced save
  const debouncedSave = useMemo(() => debounceAndSaveArgs(save, 750), [save]);

  // Pending state: debounce waiting OR mutation in-flight OR waiting room has data
  const hasPendingChanges =
    debouncedSave.pending() ||
    updateMutation.isPending ||
    pendingConfigRef.current !== null;

  return {
    save: debouncedSave,
    hasPendingChanges,
    isSaving: updateMutation.isPending,
  };
};
